// @ts-ignore
import {Swiper, SwiperSlide} from 'swiper/react';
// @ts-ignore
import {Navigation, SwiperClass} from 'swiper';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import {Text} from '~/components';
import clsx from 'clsx';
import {Fragment, useEffect, useId, useMemo, useRef, useState} from 'react';
import {
  ArrowLeftButton,
  ArrowRightButton,
  PageSectionContainer,
  ProductCard,
} from '~/components';
import {ProductCarouselEntity} from '~/types/ProductCarouselEntity';
import Tabs from '~/components/elements/Tabs.client';
// @ts-ignore
import {Tab} from '@headlessui/react';
import sanityConfig from '../../../sanity.config';
import SanityImage from '~/components/media/SanityImage.client';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import Spinner from '../elements/Spinner';
import {DATA_TEST_IDS} from '~/lib/const';

export function ProductCarousel({
  title,
  icon,
  description,
  productGroups,
  rebuyRequestMap,
  isLoading,
  disableMargin = false,
}: ProductCarouselEntity & {
  rebuyRequestMap: Record<string, Product[]>;
  disableMargin?: boolean;
}) {
  const Container = disableMargin ? Fragment : PageSectionContainer;
  const [swiper, setSwiper] = useState<SwiperClass>({});
  const [disableNextButton, setDisableNextButton] = useState(false);
  const hideHeader = productGroups.length < 2;

  const tabEnd = useMemo(
    () => (
      <div
        className={clsx(
          'items-center space-x-5 hidden md:flex pr-5 xl:pr-[97px] 3xl:mr-[calc((100vw_-_1728px)_/_2)]',
          hideHeader ? (description ? '-mt-[58px]' : '-mt-[75px]') : '',
        )}
      >
        {swiper && (
          <>
            <ArrowLeftButton onClick={() => swiper?.slidePrev()} />
            <ArrowRightButton
              onClick={() => {
                if (!disableNextButton) {
                  swiper.slideNext();
                }
              }}
            />
          </>
        )}
      </div>
    ),
    [swiper, disableNextButton, hideHeader, description],
  );

  return (
    <Container testid={DATA_TEST_IDS.PRODUCT_CAROUSEL} rightAlign>
      <div className="flex flex-col max-w-full w-[748px] pr-5">
        <div className={'flex justify-start align-center'}>
          {title && (
            <Text testid={DATA_TEST_IDS.PRODUCT_CAROUSEL_TITLE} as="h3">
              {title.toUpperCase()}
            </Text>
          )}
          {icon && (
            <div className={'relative h-[1.875rem] xl:h-[2rem] ml-2.5'}>
              <SanityImage
                dataset={sanityConfig.dataset}
                projectId={sanityConfig.projectId}
                objectFit="cover"
                height={'30px'}
                layout="fill"
                crop={icon?.crop}
                hotspot={icon?.hotspot}
                src={icon?.asset._ref}
                breakpoints={[[355, 355]]}
                alt={icon?.altText ?? title}
              />
            </div>
          )}
        </div>
        {description && (
          <div className={clsx(title && 'mt-2.5 xl:mt-5')}>
            <Text
              testid={DATA_TEST_IDS.PRODUCT_CAROUSEL_DESCRIPTION}
              as="p"
              className={'text-sm leading-[19px]'}
            >
              {description}
            </Text>
          </div>
        )}
      </div>
      <Tabs
        // `md` because of arrow buttons are hidden below md
        className={'mt-5 md:mt-2.5'}
        titles={productGroups.map((i) => i.title)}
        tabEnd={tabEnd}
        hideHeader={hideHeader}
      >
        {productGroups.map((productGroup) => (
          <Tab.Panel
            data-testid={DATA_TEST_IDS.TAB}
            key={productGroup.name}
            className={clsx(
              description ? 'mt-5 xl:mt-[1.875rem]' : 'mt-3 xl:mt-[50px]',
            )}
          >
            {isLoading ? (
              <div className="w-[355px] h-[465px] flex items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <ProductTabCarousel
                disableNextButton={disableNextButton}
                setDisableNextButton={setDisableNextButton}
                setSwiper={(swiper) => setSwiper(swiper)}
                products={rebuyRequestMap[productGroup.rebuyCustomID ?? '']}
              />
            )}
          </Tab.Panel>
        ))}
      </Tabs>
    </Container>
  );
}

function ProductTabCarousel({
  products,
  disableNextButton,
  setSwiper,
  setDisableNextButton,
}: {
  products?: Product[];
  disableNextButton: boolean;
  setSwiper: (s: SwiperClass) => void;
  setDisableNextButton: (s: boolean) => void;
}) {
  const blankSlideId = useId();
  const swiperRef = useRef<SwiperClass | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (swiperRef.current?.animating === true) {
          setDisableNextButton(true);
        }
      } else {
        setDisableNextButton(false);
      }
    });

    const element = document.getElementById(blankSlideId);

    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [blankSlideId, setDisableNextButton]);

  return (
    <div data-testid={DATA_TEST_IDS.PRODUCT_TAB_CAROUSEL}>
      <Swiper
        onSwiper={(s) => {
          setSwiper(s);
          swiperRef.current = s;
        }}
        className={'carousel-row'}
        // @ts-expect-error @headlessui/react
        modules={[Navigation]}
        slidesPerView={'auto'}
        slidesPerGroupAuto={true}
        spaceBetween={18}
        effect="fade"
        shortSwipes={true}
        threshold={10}
        allowTouchMove={true}
        allowSlideNext={!disableNextButton}
      >
        {products?.map((product) => (
          <SwiperSlide
            key={product.handle}
            className={'!w-[253px] xl:!w-[355px]'}
          >
            {/* This is needed for initial load to look good */}
            <div className={'w-[253px] xl:w-[355px]'}>
              <ProductCard product={product} />
            </div>
          </SwiperSlide>
        ))}

        <SwiperSlide
          key={blankSlideId}
          id={blankSlideId}
          className={'!w-[253px] xl:!w-[355px]'}
        />
      </Swiper>
    </div>
  );
}
